import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const KeyServices = ({
  section,
  columns = 3,
  arrowIcon = false,
  cardHeight = "30vh",
  imgHeight = "32.5vh",
  bgColor = "#F2F4FF",
}) => {
  const scrollRef = useRef(null);

  const [isLg, setIsLg] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsLg(window.innerWidth >= 1024); // lg breakpoint
    };

    handleResize(); // initial check
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const showArrows = arrowIcon;
  const { items = [] } = section || {}; // ✅ safe destructure

  /* 🔍 CHECK SCROLL POSITION */
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    setIsLeftDisabled(scrollLeft <= 0);
    setIsRightDisabled(scrollLeft >= maxScrollLeft - 5);
  };

  /* 🔥 EFFECT (NO CONDITIONAL HOOK ERROR) */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  /* 🔥 SCROLL FUNCTION */
  const scroll = (dir) => {
    const container = scrollRef.current;
    if (!container) return;

    const firstCard = container.querySelector(".card-item");
    if (!firstCard) return;

    const gap = 24; // gap-6
    const cardWidth = firstCard.offsetWidth + gap;

    container.scrollBy({
      left: dir === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };
  const shouldShowArrows = arrowIcon || !isLg;
  // ✅ EARLY RETURN AFTER HOOKS
  if (!section) return null;

  return (
    <div className="relative">
      {/* 🔹 Left Arrow */}

      {shouldShowArrows && (
        <button
          onClick={() => scroll("left")}
          disabled={isLeftDisabled}
          className={`absolute left-0 top-1/4 -translate-y-1/2 z-10 p-2 rounded-full shadow
        ${showArrows ? "flex" : "lg:hidden flex"}
        ${isLeftDisabled ? "opacity-40 cursor-not-allowed" : "text-white"}
      `}
          style={{
            background: isLeftDisabled ? "#ccc" : "var(--primary-color)",
          }}
        >
          <ChevronLeft />
        </button>
      )}

      {/* 🔹 Scroll Container */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto  box-border scroll-smooth no-scrollbar snap-x snap-mandatory"
      >
        {items.map((item, index) => (
          <div
            key={item.id || index}
            className={`
              card-item
              snap-start
              flex-shrink-0 
              w-full 
           sm:w-[calc(50%-12px)]
md:w-[calc(50%-12px)]
              ${columns === 5 ? "lg:w-[calc((100%-72px)/5)]" : ""}
              ${columns === 4 ? "lg:w-[calc((100%-72px)/4)]" : ""}
              ${columns === 3 ? "lg:w-[calc((100%-72px)/3)]" : ""}
              ${columns === 2 ? "lg:w-[calc((100%-72px)/2)]" : ""}
            `}
          >
            <ServiceCard
              item={item}
              imgHeight={imgHeight}
              bgColor={bgColor}
              cardHeight={cardHeight}
              isLg={isLg}
            />
          </div>
        ))}
      </div>

      {/* 🔹 Right Arrow */}
      {shouldShowArrows && (
        <button
          onClick={() => scroll("right")}
          disabled={isRightDisabled}
          className={`absolute right-0 top-1/4 -translate-y-1/2 z-10 p-2 rounded-full shadow
        ${showArrows ? "flex" : "lg:hidden flex"}
        ${isRightDisabled ? "opacity-40 cursor-not-allowed" : "text-white"}
      `}
          style={{
            background: isRightDisabled ? "#ccc" : "var(--primary-color)",
          }}
        >
          <ChevronRight />
        </button>
      )}
    </div>
  );
};

export default KeyServices;

/* ── Card Component ── */

const ServiceCard = ({ item, imgHeight, bgColor, cardHeight, isLg }) => {
  return (
    <div className="bg-white shadow-sm rounded-t-[0.6vw] mb-4 md:mb-5 lg:mb-0">
      {/* Image */}
      {item.image && (
        <a
          href={item.href || "#"}
          className="relative block rounded-xl rounded-b-none overflow-hidden group"
        >
          <img
            src={item.image}
            alt={item.label || item.title}
            style={{ height: isLg ? imgHeight : "12.5rem" }} // h-50 = 12.5rem
            className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />

          {item.imgtitle && (
            <div
              className="absolute top-0 left-0 px-4 py-2 text-white rounded-md"
              style={{ backgroundColor: "var(--primary-color)" }}
            >
              {item.imgtitle}
            </div>
          )}
        </a>
      )}

      {/* Content (Responsive height improvement) */}
      <div
        className=" bg-[#EAF1FF]  px-4 py-4 
          md:px-5 md:py-5 
          lg:px-[1.5vw] lg:pt-[1.5vw] lg:pb-[2vw]
        "
      >
        <div
          className=" text-center lg:text-left      "
          style={{ height: isLg ? cardHeight : "auto" }}
        >
          {item.title2 && (
            <h4 className="font-semibold mb-2  uppercase">{item.title2}</h4>
          )}

          {item.title && <h3 className="font-semibold mb-2">{item.title}</h3>}

          {item.description && (
            <p className="leading-relaxed mb-4 ">{item.description}</p>
          )}
        </div>

        {item.btnLabel && (
          <button
            className="px-3 py-2 rounded-xl text-white w-full sm:w-auto"
            style={{ background: "var(--primary-color)" }}
          >
            {item.btnLabel}
          </button>
        )}
      </div>
    </div>
  );
};
